import {
  cloneStateValue,
  documentsEqual,
  restoreStateSlice,
  type HistoryDir,
} from "./stateKindRegistry";
import {
  getRegisteredStateRoots,
  getStateRootByUuid,
  getStateSlice,
  type SugarObject,
  type SugarStateSlice,
} from "./SugarObject";

export type StateDocument = Record<string, SugarStateSlice>;

export function captureDocument(roots?: readonly SugarObject[]): StateDocument {
  const list = roots ?? getRegisteredStateRoots();
  const doc: StateDocument = {};
  for (const obj of list) {
    const slice = getStateSlice(obj);
    if (!slice) continue;
    doc[obj.uuid] = {
      kind: slice.kind,
      value: cloneStateValue(slice.value),
    };
  }
  return doc;
}

export function cloneDocument(document: StateDocument): StateDocument {
  const out: StateDocument = {};
  for (const [uuid, slice] of Object.entries(document)) {
    out[uuid] = {
      kind: slice.kind,
      value: cloneStateValue(slice.value),
    };
  }
  return out;
}

function findSingleRootByKind(kind: string): SugarObject | undefined {
  let found: SugarObject | undefined;
  for (const root of getRegisteredStateRoots()) {
    if (getStateSlice(root)?.kind !== kind) continue;
    if (found) return undefined;
    found = root;
  }
  return found;
}

export function applyDocument(
  document: StateDocument,
  dir: HistoryDir,
  opts?: { fallbackByKind?: boolean },
): void {
  const fallbackByKind = opts?.fallbackByKind ?? true;
  const seen = new Set<string>();

  for (const [uuid, slice] of Object.entries(document)) {
    let target = getStateRootByUuid(uuid);
    if (!target && fallbackByKind) {
      target = findSingleRootByKind(slice.kind);
    }
    if (!target) continue;
    seen.add(target.uuid);
    restoreStateSlice(target, slice, dir);
  }

  for (const root of getRegisteredStateRoots()) {
    if (seen.has(root.uuid)) continue;
    const current = getStateSlice(root);
    if (!current) continue;
    restoreStateSlice(
      root,
      { kind: current.kind, value: emptyValueForKind(current.kind) },
      dir,
    );
  }
}

function emptyValueForKind(kind: string): unknown {
  if (kind === "planarGraph") return [];
  if (kind === "room") {
    return { products: [], shape: { points: [], lines: [] } };
  }
  if (kind === "roomProducts") return [];
  if (kind === "productMeasurements") return [];
  if (kind === "configuratorComposition") {
    return { rootModelUuids: [], configurators: [] };
  }
  if (kind === "sugarModel") {
    return {
      partMaterials: [],
      transform: { p: [0, 0, 0], q: [0, 0, 0, 1] },
    };
  }
  if (kind === "sugarModelConfigurator") {
    return {
      model: {
        partMaterials: [],
        transform: { p: [0, 0, 0], q: [0, 0, 0, 1] },
      },
      mainConfig: [],
      sideConfig: [],
      materialConfig: [],
    };
  }
  return null;
}
export { documentsEqual };
